import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Edit } from "lucide-react";
import { Link } from "react-router-dom";
import { DrillCardActions } from "./DrillCardActions";
import { DrillPreviewSection } from "./DrillPreviewSection";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DrillCardProps {
  drill: any;
  isAdmin: boolean;
  isCreator: boolean;
  onPreview: (drill: any) => void;
  onSelect: (drill: any) => void;
  onDownload: (drill: any) => void;
  onShare: () => void;
  onDelete: (id: string) => void;
}

export const DrillCard = ({
  drill,
  isAdmin,
  onPreview,
  onSelect,
  onDownload,
  onShare,
  onDelete,
}: DrillCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(drill.title);
  const { toast } = useToast();

  const handleTitleUpdate = async () => {
    try {
      const { error } = await supabase
        .from("drills")
        .update({ title })
        .eq("id", drill.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Drill title updated successfully!",
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update drill title.",
      });
    }
  };

  return (
    <Card className="border border-gray-200">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="flex-1">
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="max-w-[200px]"
                />
                <Button size="sm" onClick={handleTitleUpdate}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">{drill.title}</CardTitle>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="p-0 h-auto hover:bg-transparent"
                  >
                    <Edit className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <DrillCardActions
            onPreview={() => onPreview(drill)}
            onSelect={() => onSelect(drill)}
            onDownload={() => onDownload(drill)}
            onShare={onShare}
            onDelete={() => onDelete(drill.id)}
          />
        </div>
      </CardHeader>
      <CardContent>
        <DrillPreviewSection
          previewImageUrl={drill.preview_image_url}
          description={drill.description}
          onPreview={() => onPreview(drill)}
        />
        <div className="flex justify-end mt-4">
          <Button
            variant="outline"
            size="sm"
            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
            asChild
          >
            <Link to="/marketplace">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Go to Marketplace
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};